// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\RepairableSparePartForm.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/repairableSpareParts.css";

const EMPTY_FORM = {
  material_id: "",
  branch_name: "",
  warehouse_id: "",
  quantity: "1",
  serial_number: "",
  equipment_code: "",
  supplier_source: "REGISTRO_MANUAL",
  supplier_id: "",
  supplier_name: "",
  supplier_contact_name: "",
  supplier_phone: "",
  supplier_email: "",
  failure_description: "",
  requested_work: "",
  responsible_user_id: "",
  previous_case_id: "",
  previous_case_relation_type: "",
  is_warranty: false,
  warranty_reference: "",
  warranty_notes: "",
  warranty_expires_at: "",
  sent_at: "",
  delivery_note_number: "",
  transport_company: "",
  tracking_number: "",
  shipment_contact: "",
  shipment_notes: "",
  estimated_return_date: "",
  received_at: "",
  supplier_diagnosis: "",
  performed_repair: "",
  reception_result: "",
  final_notes: "",
  quote_number: "",
  quote_received_at: "",
  quoted_amount: "",
  purchase_order_number: "",
  purchase_order_source: "",
  final_amount: "",
  currency: "EUR",
};

const CREATE_FIELDS = [
  "material_id",
  "branch_name",
  "warehouse_id",
  "quantity",
  "serial_number",
  "equipment_code",
  "supplier_source",
  "supplier_id",
  "supplier_name",
  "supplier_contact_name",
  "supplier_phone",
  "supplier_email",
  "failure_description",
  "requested_work",
  "responsible_user_id",
  "previous_case_id",
  "previous_case_relation_type",
  "is_warranty",
  "warranty_reference",
  "warranty_notes",
  "warranty_expires_at",
];

const TECHNICIAN_EDIT_FIELDS = [
  "quantity",
  "serial_number",
  "equipment_code",
  "failure_description",
  "requested_work",
  "responsible_user_id",
  "previous_case_id",
  "previous_case_relation_type",
  "is_warranty",
  "warranty_reference",
  "warranty_notes",
  "warranty_expires_at",
];

const ADMIN_EDIT_FIELDS = [
  ...TECHNICIAN_EDIT_FIELDS,
  "supplier_source",
  "supplier_id",
  "supplier_name",
  "supplier_contact_name",
  "supplier_phone",
  "supplier_email",
  "sent_at",
  "delivery_note_number",
  "transport_company",
  "tracking_number",
  "shipment_contact",
  "shipment_notes",
  "estimated_return_date",
  "received_at",
  "supplier_diagnosis",
  "performed_repair",
  "reception_result",
  "final_notes",
  "quote_number",
  "quote_received_at",
  "quoted_amount",
  "purchase_order_number",
  "purchase_order_source",
  "final_amount",
  "currency",
];

const toDateInput = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const buildInitialForm = (repairCase) => {
  if (!repairCase) {
    return { ...EMPTY_FORM };
  }

  return {
    ...EMPTY_FORM,
    material_id: repairCase.material_snapshot?.material_id || "",
    branch_name: repairCase.location?.branch_name || "",
    warehouse_id: repairCase.location?.warehouse_id || "",
    quantity: String(repairCase.quantity ?? 1),
    serial_number: repairCase.serial_number || "",
    equipment_code: repairCase.equipment_code || "",
    supplier_source: repairCase.supplier?.source || "REGISTRO_MANUAL",
    supplier_id: repairCase.supplier?.id || "",
    supplier_name: repairCase.supplier?.name || "",
    supplier_contact_name: repairCase.supplier?.contact_name || "",
    supplier_phone: repairCase.supplier?.phone || "",
    supplier_email: repairCase.supplier?.email || "",
    failure_description: repairCase.technical?.failure_description || "",
    requested_work: repairCase.technical?.requested_work || "",
    responsible_user_id: repairCase.responsibility?.responsible_user?.id
      ? String(repairCase.responsibility.responsible_user.id)
      : "",
    previous_case_id: repairCase.relation?.previous_case_id
      ? String(repairCase.relation.previous_case_id)
      : "",
    previous_case_relation_type: repairCase.relation?.type || "",
    is_warranty: Boolean(repairCase.warranty?.is_warranty),
    warranty_reference: repairCase.warranty?.reference || "",
    warranty_notes: repairCase.warranty?.notes || "",
    warranty_expires_at: toDateInput(repairCase.warranty?.expires_at),
    sent_at: toDateInput(repairCase.shipment?.sent_at),
    delivery_note_number: repairCase.shipment?.delivery_note_number || "",
    transport_company: repairCase.shipment?.transport_company || "",
    tracking_number: repairCase.shipment?.tracking_number || "",
    shipment_contact: repairCase.shipment?.shipment_contact || "",
    shipment_notes: repairCase.shipment?.shipment_notes || "",
    estimated_return_date: toDateInput(repairCase.shipment?.estimated_return_date),
    received_at: toDateInput(repairCase.shipment?.received_at),
    supplier_diagnosis: repairCase.technical?.supplier_diagnosis || "",
    performed_repair: repairCase.technical?.performed_repair || "",
    reception_result: repairCase.technical?.reception_result || "",
    final_notes: repairCase.technical?.final_notes || "",
    quote_number: repairCase.economics?.quote_number || "",
    quote_received_at: toDateInput(repairCase.economics?.quote_received_at),
    quoted_amount: repairCase.economics?.quoted_amount || "",
    purchase_order_number: repairCase.economics?.purchase_order_number || "",
    purchase_order_source: repairCase.economics?.purchase_order_source || "",
    final_amount: repairCase.economics?.final_amount || "",
    currency: repairCase.economics?.currency || "EUR",
  };
};

const cleanPayload = (form, fields) => {
  const payload = {};

  fields.forEach((field) => {
    const value = form[field];

    if (field === "is_warranty") {
      payload[field] = Boolean(value);
      return;
    }

    if (field === "quantity") {
      payload[field] = Number(value);
      return;
    }

    if (field === "responsible_user_id" || field === "previous_case_id") {
      payload[field] = value === "" ? null : Number(value);
      return;
    }

    payload[field] = typeof value === "string" && value.trim() === ""
      ? null
      : typeof value === "string"
        ? value.trim()
        : value;
  });

  return payload;
};

const getOrmaRows = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const getDescription = (row) =>
  row?.master_description ||
  row?.visor_description ||
  row?.description ||
  "Sin descripción";

const normalizeUsers = (data) => {
  const candidates = Array.isArray(data)
    ? data
    : Array.isArray(data?.users)
      ? data.users
      : Array.isArray(data?.items)
        ? data.items
        : [];

  return candidates.filter(
    (user) =>
      user?.is_active !== false &&
      ["admin", "tecnico"].includes(String(user?.role || "").toLowerCase()),
  );
};

const RepairableSparePartForm = ({
  embedded = false,
  initialCase = null,
  options: providedOptions = null,
  economics = null,
  onSuccess,
  onCancel,
}) => {
  const { store, actions } = useContext(Context);
  const navigate = useNavigate();
  const isEdit = Boolean(initialCase?.id);
  const role = String(store.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();
  const isAdmin = role === "admin";

  const [form, setForm] = useState(() =>
    buildInitialForm(
      economics && initialCase
        ? {
            ...initialCase,
            economics,
          }
        : initialCase,
    ),
  );
  const [moduleOptions, setModuleOptions] = useState(providedOptions);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialSearch, setMaterialSearch] = useState("");
  const [materialRows, setMaterialRows] = useState([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialError, setMaterialError] = useState("");
  const [responsibleUsers, setResponsibleUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(!providedOptions);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    setForm(
      buildInitialForm(
        economics && initialCase
          ? {
              ...initialCase,
              economics,
            }
          : initialCase,
      ),
    );
  }, [economics, initialCase]);

  useEffect(() => {
    if (providedOptions) {
      setModuleOptions(providedOptions);
      return undefined;
    }

    const controller = new AbortController();

    const loadOptions = async () => {
      setLoadingOptions(true);
      const result = await actions.getRepairableSparePartOptions({
        signal: controller.signal,
      });

      if (!controller.signal.aborted) {
        if (result.success) {
          setModuleOptions(result.data);
        } else if (!result.aborted) {
          setFormError(result.error);
        }
        setLoadingOptions(false);
      }
    };

    loadOptions();
    return () => controller.abort();
  }, [actions, providedOptions]);

  useEffect(() => {
    const controller = new AbortController();

    const loadIdentity = async () => {
      const result = await actions.getRepairableCurrentUser({
        signal: controller.signal,
      });

      if (result.success && !controller.signal.aborted) {
        setCurrentUser(result.data?.user || result.data);
      }
    };

    loadIdentity();
    return () => controller.abort();
  }, [actions]);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const controller = new AbortController();

    const loadUsers = async () => {
      const result = await actions.getRepairableResponsibleUsers({
        signal: controller.signal,
      });

      if (result.success && !controller.signal.aborted) {
        setResponsibleUsers(normalizeUsers(result.data));
      }
    };

    loadUsers();
    return () => controller.abort();
  }, [actions, isAdmin]);

  useEffect(() => {
    if (isEdit || materialSearch.trim().length < 2) {
      setMaterialRows([]);
      setMaterialError("");
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setMaterialLoading(true);
      setMaterialError("");

      const result = await actions.searchRepairableOrmaMaterials(
        materialSearch.trim(),
        { signal: controller.signal },
      );

      if (!controller.signal.aborted) {
        if (result.success) {
          setMaterialRows(getOrmaRows(result));
        } else if (!result.aborted) {
          setMaterialError(result.error);
          setMaterialRows([]);
        }
        setMaterialLoading(false);
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [actions, isEdit, materialSearch]);

  const availableLocations = useMemo(() => {
    if (!selectedMaterial) return [];

    if (Array.isArray(selectedMaterial.locations)) {
      return selectedMaterial.locations;
    }

    return [selectedMaterial];
  }, [selectedMaterial]);

  const supplierSources = moduleOptions?.supplier_sources || [
    "CATALOGO_IMPORTADO",
    "REGISTRO_MANUAL",
  ];
  const relationTypes = moduleOptions?.case_relation_types || [];
  const purchaseOrderSources = moduleOptions?.purchase_order_sources || [];

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    setForm((current) => {
      const next = {
        ...current,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "serial_number" && value.trim()) {
        next.quantity = "1";
      }

      if (name === "is_warranty" && !checked) {
        next.warranty_reference = "";
        next.warranty_notes = "";
        next.warranty_expires_at = "";
      }

      return next;
    });
  };

  const selectMaterial = (row) => {
    setSelectedMaterial(row);
    setMaterialSearch(`${row.material_id} — ${getDescription(row)}`);
    setMaterialRows([]);

    setForm((current) => ({
      ...current,
      material_id: row.material_id || "",
      branch_name: row.branch_name || "",
      warehouse_id: row.warehouse_id || "",
    }));
  };

  const assignCurrentUser = () => {
    if (!currentUser?.id) return;

    setForm((current) => ({
      ...current,
      responsible_user_id: String(currentUser.id),
    }));
  };

  const validate = () => {
    const nextWarnings = [];

    if (!isEdit && !form.material_id) {
      return "Debes seleccionar un material del catálogo ORMA";
    }

    if (!Number.isInteger(Number(form.quantity)) || Number(form.quantity) <= 0) {
      return "La cantidad debe ser un número entero positivo";
    }

    if (form.serial_number.trim() && Number(form.quantity) !== 1) {
      return "Los repuestos con número de serie deben tener cantidad 1";
    }

    if (!form.failure_description.trim()) {
      return "Describe la avería o motivo de reparación";
    }

    if (!form.requested_work.trim()) {
      return "Indica el trabajo solicitado";
    }

    if (!form.serial_number.trim()) {
      nextWarnings.push(
        "El material no tiene número de serie. Se registrará como cantidad agrupada.",
      );
    }

    if (
      form.supplier_source === "REGISTRO_MANUAL" &&
      !form.supplier_name.trim()
    ) {
      nextWarnings.push(
        "No se ha indicado proveedor. Puede completarse durante la gestión administrativa.",
      );
    }

    setWarnings(nextWarnings);
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);

    const fields = isEdit
      ? isAdmin
        ? ADMIN_EDIT_FIELDS
        : TECHNICIAN_EDIT_FIELDS
      : CREATE_FIELDS;

    const payload = cleanPayload(form, fields);
    const result = isEdit
      ? await actions.updateRepairableSparePart(initialCase.id, payload)
      : await actions.createRepairableSparePart(payload);

    setSubmitting(false);

    if (!result.success) {
      const duplicateMessage =
        result.code === "ACTIVE_MATERIAL_SERIAL_DUPLICATE"
          ? "Ya existe un expediente activo para este material y número de serie."
          : result.error;
      setFormError(duplicateMessage);
      return;
    }

    const responseCase = result.data?.case || result.data;

    if (onSuccess) {
      onSuccess(responseCase, result.data);
      return;
    }

    if (responseCase?.id) {
      navigate(`/reparable-spare-parts/${responseCase.id}`);
    } else {
      navigate("/reparable-spare-parts");
    }
  };

  const renderResponsibleField = () => {
    if (isAdmin && responsibleUsers.length > 0) {
      return (
        <select
          id="repair_responsible_user_id"
          name="responsible_user_id"
          value={form.responsible_user_id}
          onChange={handleChange}
        >
          <option value="">Sin responsable asignado</option>
          {responsibleUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.username || user.email || `Usuario ${user.id}`} — {user.role}
            </option>
          ))}
        </select>
      );
    }

    return (
      <div className="repairable-inline-control">
        <input
          id="repair_responsible_user_id"
          name="responsible_user_id"
          type="number"
          min="1"
          value={form.responsible_user_id}
          onChange={handleChange}
          placeholder="ID de usuario (opcional)"
        />
        {currentUser?.id && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={assignCurrentUser}
          >
            Asignarme
          </button>
        )}
      </div>
    );
  };

  const formBody = (
    <form className={`repairable-form${advancedOpen ? " repairable-form-advanced-open" : ""}`} onSubmit={handleSubmit} noValidate>
      {!isEdit && (
        <section className="repairable-card repairable-form-section">
          <div className="repairable-section-heading">
            <div>
              <span className="repairable-eyebrow">01 · MATERIAL ORMA</span>
              <h2>Identificación del repuesto</h2>
            </div>
          </div>

          <div className="repairable-form-grid repairable-form-grid-wide">
            <div className="repairable-field repairable-field-span-2">
              <label htmlFor="repair_material_search">
                Buscar material <span aria-hidden="true">*</span>
              </label>
              <input
                id="repair_material_search"
                type="search"
                value={materialSearch}
                onChange={(event) => {
                  setMaterialSearch(event.target.value);
                  setSelectedMaterial(null);
                  setForm((current) => ({
                    ...current,
                    material_id: "",
                    branch_name: "",
                    warehouse_id: "",
                  }));
                }}
                placeholder="ID material, descripción, marca o referencia"
                autoComplete="off"
              />
              <small>La selección se realiza sobre la última carga ORMA utilizable.</small>

              {materialLoading && (
                <div className="repairable-inline-state">Buscando materiales…</div>
              )}

              {materialError && (
                <div className="repairable-inline-state repairable-state-error">
                  {materialError}
                </div>
              )}

              {materialRows.length > 0 && (
                <div className="repairable-material-results" role="listbox">
                  {materialRows.map((row, index) => (
                    <button
                      key={`${row.id || row.material_id}-${index}`}
                      type="button"
                      className="repairable-material-option"
                      onClick={() => selectMaterial(row)}
                    >
                      <strong>{row.material_id}</strong>
                      <span>{getDescription(row)}</span>
                      <small>
                        {[row.brand, row.model, row.manufacturer_ref]
                          .filter(Boolean)
                          .join(" · ") || "Sin datos de fabricante"}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedMaterial && (
              <div className="repairable-selection-summary repairable-field-span-2">
                <div>
                  <span>Material seleccionado</span>
                  <strong>{selectedMaterial.material_id}</strong>
                </div>
                <p>{getDescription(selectedMaterial)}</p>
              </div>
            )}

            <div className="repairable-field">
              <label htmlFor="repair_branch_name">Sucursal</label>
              <input
                id="repair_branch_name"
                name="branch_name"
                value={form.branch_name}
                onChange={handleChange}
                list="repair_branch_options"
                placeholder="Sucursal ORMA"
              />
              <datalist id="repair_branch_options">
                {availableLocations.map((location, index) => (
                  <option
                    key={`${location.branch_name || "branch"}-${index}`}
                    value={location.branch_name || ""}
                  />
                ))}
              </datalist>
            </div>

            <div className="repairable-field">
              <label htmlFor="repair_warehouse_id">Almacén</label>
              <input
                id="repair_warehouse_id"
                name="warehouse_id"
                value={form.warehouse_id}
                onChange={handleChange}
                list="repair_warehouse_options"
                placeholder="Almacén ORMA"
              />
              <datalist id="repair_warehouse_options">
                {availableLocations.map((location, index) => (
                  <option
                    key={`${location.warehouse_id || "warehouse"}-${index}`}
                    value={location.warehouse_id || ""}
                  />
                ))}
              </datalist>
            </div>
          </div>
        </section>
      )}

      {isEdit && (
        <section className="repairable-card repairable-form-section">
          <span className="repairable-eyebrow">MATERIAL VINCULADO</span>
          <div className="repairable-fixed-material">
            <strong>{initialCase.material_snapshot?.material_id}</strong>
            <span>{initialCase.material_snapshot?.description || "Sin descripción"}</span>
          </div>
        </section>
      )}

      <section className="repairable-card repairable-form-section">
        <div className="repairable-section-heading">
          <div>
            <span className="repairable-eyebrow">02 · IDENTIFICACIÓN FÍSICA</span>
            <h2>Cantidad y trazabilidad</h2>
          </div>
        </div>

        <div className="repairable-form-grid">
          <div className="repairable-field">
            <label htmlFor="repair_quantity">
              Cantidad <span aria-hidden="true">*</span>
            </label>
            <input
              id="repair_quantity"
              name="quantity"
              type="number"
              min="1"
              step="1"
              value={form.quantity}
              onChange={handleChange}
              disabled={Boolean(form.serial_number.trim())}
              required
            />
          </div>

          <div className="repairable-field">
            <label htmlFor="repair_serial_number">Número de serie</label>
            <input
              id="repair_serial_number"
              name="serial_number"
              value={form.serial_number}
              maxLength={255}
              onChange={handleChange}
              placeholder="Opcional"
            />
            <small>Al informar una serie, la cantidad se fija en 1.</small>
          </div>

          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="repair_equipment_code">Equipo o instalación</label>
            <input
              id="repair_equipment_code"
              name="equipment_code"
              value={form.equipment_code}
              onChange={handleChange}
              placeholder="Código del equipo donde estaba instalado"
            />
          </div>
        </div>
      </section>

      {(!isEdit || isAdmin) && (
        <section className="repairable-card repairable-form-section">
          <div className="repairable-section-heading">
            <div>
              <span className="repairable-eyebrow">03 · PROVEEDOR</span>
              <h2>Proveedor reparador</h2>
            </div>
          </div>

          <div className="repairable-form-grid">
            <div className="repairable-field">
              <label htmlFor="repair_supplier_source">Origen del proveedor</label>
              <select
                id="repair_supplier_source"
                name="supplier_source"
                value={form.supplier_source}
                onChange={handleChange}
              >
                {supplierSources.map((source) => (
                  <option key={source} value={source}>
                    {source.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </div>

            <div className="repairable-field">
              <label htmlFor="repair_supplier_id">ID proveedor</label>
              <input
                id="repair_supplier_id"
                name="supplier_id"
                value={form.supplier_id}
                onChange={handleChange}
                placeholder="Identificador externo"
              />
            </div>

            <div className="repairable-field repairable-field-span-2">
              <label htmlFor="repair_supplier_name">Nombre del proveedor</label>
              <input
                id="repair_supplier_name"
                name="supplier_name"
                value={form.supplier_name}
                onChange={handleChange}
                placeholder="Razón social o nombre comercial"
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repair_supplier_contact_name">Contacto</label>
              <input
                id="repair_supplier_contact_name"
                name="supplier_contact_name"
                value={form.supplier_contact_name}
                onChange={handleChange}
              />
            </div>

            <div className="repairable-field">
              <label htmlFor="repair_supplier_phone">Teléfono</label>
              <input
                id="repair_supplier_phone"
                name="supplier_phone"
                value={form.supplier_phone}
                onChange={handleChange}
              />
            </div>

            <div className="repairable-field repairable-field-span-2">
              <label htmlFor="repair_supplier_email">Correo electrónico</label>
              <input
                id="repair_supplier_email"
                name="supplier_email"
                type="email"
                value={form.supplier_email}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>
      )}


      <div className="repairable-advanced-toggle-card">
        <div>
          <strong>Opciones avanzadas</strong>
          <p>
            Logística, garantía, relaciones, diagnóstico y datos económicos.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setAdvancedOpen((current) => !current)}
          aria-expanded={advancedOpen}
        >
          <i
            className={
              advancedOpen
                ? "bi bi-chevron-up"
                : "bi bi-chevron-down"
            }
            aria-hidden="true"
          ></i>
          {advancedOpen ? "Ocultar opciones" : "Mostrar opciones"}
        </button>
      </div>

<section className="repairable-card repairable-form-section repairable-form-section-advanced">
        <div className="repairable-section-heading">
          <div>
            <span className="repairable-eyebrow">04 · AVERÍA</span>
            <h2>Diagnóstico inicial y trabajo solicitado</h2>
          </div>
        </div>

        <div className="repairable-form-grid">
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="repair_failure_description">
              Avería o motivo <span aria-hidden="true">*</span>
            </label>
            <textarea
              id="repair_failure_description"
              name="failure_description"
              rows="4"
              value={form.failure_description}
              onChange={handleChange}
              required
            />
          </div>

          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="repair_requested_work">
              Trabajo solicitado <span aria-hidden="true">*</span>
            </label>
            <textarea
              id="repair_requested_work"
              name="requested_work"
              rows="4"
              value={form.requested_work}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </section>

      <section className="repairable-card repairable-form-section repairable-form-section-advanced">
        <div className="repairable-section-heading">
          <div>
            <span className="repairable-eyebrow">05 · RESPONSABILIDAD</span>
            <h2>Responsable y relación histórica</h2>
          </div>
        </div>

        <div className="repairable-form-grid">
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="repair_responsible_user_id">Responsable</label>
            {renderResponsibleField()}
          </div>

          <div className="repairable-field">
            <label htmlFor="repair_previous_case_id">Expediente anterior</label>
            <input
              id="repair_previous_case_id"
              name="previous_case_id"
              type="number"
              min="1"
              value={form.previous_case_id}
              onChange={handleChange}
              placeholder="ID interno"
            />
          </div>

          <div className="repairable-field">
            <label htmlFor="repair_previous_case_relation_type">
              Tipo de relación
            </label>
            <select
              id="repair_previous_case_relation_type"
              name="previous_case_relation_type"
              value={form.previous_case_relation_type}
              onChange={handleChange}
            >
              <option value="">Sin relación</option>
              {relationTypes.map((relationType) => (
                <option key={relationType} value={relationType}>
                  {relationType.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </div>

          <div className="repairable-field repairable-field-span-2">
            <label className="repairable-check">
              <input
                name="is_warranty"
                type="checkbox"
                checked={form.is_warranty}
                onChange={handleChange}
              />
              <span>Gestión asociada a garantía</span>
            </label>
          </div>

          {form.is_warranty && (
            <>
              <div className="repairable-field">
                <label htmlFor="repair_warranty_reference">
                  Referencia de garantía
                </label>
                <input
                  id="repair_warranty_reference"
                  name="warranty_reference"
                  value={form.warranty_reference}
                  onChange={handleChange}
                />
              </div>

              <div className="repairable-field">
                <label htmlFor="repair_warranty_expires_at">
                  Vencimiento
                </label>
                <input
                  id="repair_warranty_expires_at"
                  name="warranty_expires_at"
                  type="date"
                  value={form.warranty_expires_at}
                  onChange={handleChange}
                />
              </div>

              <div className="repairable-field repairable-field-span-2">
                <label htmlFor="repair_warranty_notes">Notas de garantía</label>
                <textarea
                  id="repair_warranty_notes"
                  name="warranty_notes"
                  rows="3"
                  value={form.warranty_notes}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
        </div>
      </section>

      {isEdit && isAdmin && (
        <>
          <section className="repairable-card repairable-form-section repairable-form-section-advanced">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">06 · LOGÍSTICA</span>
                <h2>Envío y recepción</h2>
              </div>
            </div>

            <div className="repairable-form-grid">
              <div className="repairable-field">
                <label htmlFor="repair_sent_at">Fecha de envío</label>
                <input
                  id="repair_sent_at"
                  name="sent_at"
                  type="date"
                  value={form.sent_at}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_estimated_return_date">Retorno previsto</label>
                <input
                  id="repair_estimated_return_date"
                  name="estimated_return_date"
                  type="date"
                  value={form.estimated_return_date}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_received_at">Fecha de recepción</label>
                <input
                  id="repair_received_at"
                  name="received_at"
                  type="date"
                  value={form.received_at}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_delivery_note_number">Albarán de salida</label>
                <input
                  id="repair_delivery_note_number"
                  name="delivery_note_number"
                  value={form.delivery_note_number}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_transport_company">Transportista</label>
                <input
                  id="repair_transport_company"
                  name="transport_company"
                  value={form.transport_company}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_tracking_number">Seguimiento</label>
                <input
                  id="repair_tracking_number"
                  name="tracking_number"
                  value={form.tracking_number}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_shipment_contact">Contacto del envío</label>
                <input
                  id="repair_shipment_contact"
                  name="shipment_contact"
                  value={form.shipment_contact}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field repairable-field-span-2">
                <label htmlFor="repair_shipment_notes">Notas del envío</label>
                <textarea
                  id="repair_shipment_notes"
                  name="shipment_notes"
                  rows="3"
                  value={form.shipment_notes}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          <section className="repairable-card repairable-form-section repairable-form-section-advanced">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">07 · REPARACIÓN</span>
                <h2>Diagnóstico y resultado</h2>
              </div>
            </div>

            <div className="repairable-form-grid">
              {[
                ["supplier_diagnosis", "Diagnóstico del proveedor"],
                ["performed_repair", "Reparación realizada"],
                ["reception_result", "Resultado de recepción"],
                ["final_notes", "Notas finales"],
              ].map(([name, label]) => (
                <div
                  className="repairable-field repairable-field-span-2"
                  key={name}
                >
                  <label htmlFor={`repair_${name}`}>{label}</label>
                  <textarea
                    id={`repair_${name}`}
                    name={name}
                    rows="3"
                    value={form[name]}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="repairable-card repairable-form-section repairable-economic-card repairable-form-section-advanced">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">08 · ECONOMÍA · ADMIN</span>
                <h2>Presupuesto, pedido e importe final</h2>
              </div>
            </div>

            <div className="repairable-form-grid">
              <div className="repairable-field">
                <label htmlFor="repair_quote_number">N.º presupuesto</label>
                <input
                  id="repair_quote_number"
                  name="quote_number"
                  value={form.quote_number}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_quote_received_at">Fecha presupuesto</label>
                <input
                  id="repair_quote_received_at"
                  name="quote_received_at"
                  type="date"
                  value={form.quote_received_at}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_quoted_amount">Importe presupuestado</label>
                <input
                  id="repair_quoted_amount"
                  name="quoted_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.quoted_amount}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_currency">Moneda</label>
                <input
                  id="repair_currency"
                  name="currency"
                  maxLength="3"
                  value={form.currency}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      currency: event.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_purchase_order_number">Pedido</label>
                <input
                  id="repair_purchase_order_number"
                  name="purchase_order_number"
                  value={form.purchase_order_number}
                  onChange={handleChange}
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_purchase_order_source">Origen pedido</label>
                <select
                  id="repair_purchase_order_source"
                  name="purchase_order_source"
                  value={form.purchase_order_source}
                  onChange={handleChange}
                >
                  <option value="">Sin origen</option>
                  {purchaseOrderSources.map((source) => (
                    <option key={source} value={source}>
                      {source.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="repairable-field">
                <label htmlFor="repair_final_amount">Importe final</label>
                <input
                  id="repair_final_amount"
                  name="final_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.final_amount}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>
        </>
      )}

      {warnings.length > 0 && (
        <div className="repairable-alert repairable-alert-warning">
          <strong>Advertencias</strong>
          <ul>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {formError && (
        <div
          className="repairable-alert repairable-alert-error"
          role="alert"
        >
          {formError}
        </div>
      )}

      <div className="repairable-form-actions">
        {embedded ? (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
        ) : (
          <Link
            className="btn btn-outline-secondary"
            to="/reparable-spare-parts"
          >
            Volver
          </Link>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || loadingOptions}
        >
          {submitting
            ? "Guardando…"
            : isEdit
              ? "Guardar cambios"
              : "Crear expediente"}
        </button>
      </div>
    </form>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <main className="container mt-5 repairable-page">
      <header className="repairable-page-header">
        <div className="repairable-title-block">
          <span className="repairable-title-icon" aria-hidden="true">
            <i className="bi bi-tools"></i>
          </span>
          <div>
            <span className="repairable-eyebrow">GESTIÓN OPERATIVA</span>
            <h1>Nuevo expediente de reparación</h1>
            <p>
              Vincula el repuesto con ORMA y registra la información técnica
              inicial.
            </p>
          </div>
        </div>
      </header>

      {loadingOptions ? (
        <div className="repairable-state">Cargando formulario…</div>
      ) : (
        formBody
      )}
    </main>
  );
};

export default RepairableSparePartForm;
